<script lang="ts">
  /**
   * Input для змінних у нодах автоматизації з live-preview результату.
   * Підтримує три режими (визначаються автоматично за вмістом):
   *   - {{ <JS> }} — JavaScript-вираз; preview показує обчислене значення
   *     або повідомлення про помилку
   *   - $.path — JSONPath-light; preview показує value з payload
   *   - решта — literal-string; preview не показуємо (тривіально)
   *
   * Preview візуалізується лише коли є sample-payload (data.lastWebhookPayload).
   */
  import { evaluateMapping, formatExprResult, type ExprResult } from '../../lib/expression-mapping';

  let {
    value,
    placeholder,
    payload,
    onInput,
  }: {
    value: string;
    placeholder?: string;
    /** Останній прийнятий webhook payload — джерело для eval. null = немає. */
    payload: unknown;
    onInput: (next: string) => void;
  } = $props();

  const result = $derived<ExprResult>(evaluateMapping(value ?? '', payload));

  /** Чи показуємо preview-плашку. Literal та empty не показуємо щоб не шуміти. */
  const showPreview = $derived(
    result.status === 'ok' || result.status === 'error',
  );

  const previewClass = $derived.by(() => {
    if (result.status === 'error') return 'bg-rose-50 border-rose-200 text-rose-700';
    if (result.value === undefined || result.value === null || result.value === '') {
      return 'bg-amber-50 border-amber-200 text-amber-700';
    }
    return 'bg-emerald-50 border-emerald-200 text-emerald-700';
  });

  /** Підказка типу значення для UI. */
  const typeBadge = $derived.by(() => {
    const v = result.value;
    if (v === undefined || v === null) return null;
    if (typeof v === 'string') return 'string';
    if (typeof v === 'number') return 'number';
    if (typeof v === 'boolean') return 'bool';
    if (Array.isArray(v)) return `array(${v.length})`;
    if (v instanceof Date) return 'date';
    if (typeof v === 'object') return 'object';
    return typeof v;
  });
</script>

<input
  value={value}
  placeholder={placeholder}
  oninput={(e) => onInput((e.currentTarget as HTMLInputElement).value)}
  class="w-full rounded-lg border border-[var(--color-border-3)] px-2.5 py-1.5 text-xs font-mono"
/>

{#if showPreview && payload !== null && payload !== undefined}
  <div
    class="mt-1 flex items-center gap-2 rounded-md border px-2 py-1 text-[10.5px] font-mono {previewClass}"
  >
    {#if result.status === 'error'}
      <span class="font-bold">✗</span>
      <span class="truncate">{result.error ?? 'syntax error'}</span>
    {:else}
      <span class="font-bold">=</span>
      <span class="flex-1 truncate">{formatExprResult(result.value)}</span>
      {#if typeBadge}
        <span class="rounded bg-white/60 px-1 text-[9px] font-bold uppercase tracking-wider opacity-70">
          {typeBadge}
        </span>
      {/if}
    {/if}
  </div>
{/if}
